/* ------------------------------------------------------------------------------------------------------------------
 * Custom (10.09.2026): Warenkorb-Drawer nach Vorbild brunathelabel.com – gehört zu sections/cart-drawer.liquid.
 *
 * <cart-drawer-recommendations>  lädt "Das könnte dir auch gefallen" (Shopify-Empfehlungen, Fallback: manuelle Auswahl)
 * <cart-delivery-estimate>       berechnet den voraussichtlichen Lieferzeitraum ("12. Sep - 15. Sep")
 * <cart-reservation>             Countdown "Artikel reserviert" (snippets/cart-drawer-reservation.liquid)
 *
 * Der Drawer selbst wird weiterhin vom Theme (theme.js, <cart-drawer>) bei jeder Änderung neu gerendert.
 * ------------------------------------------------------------------------------------------------------------------ */
(function () {
  var routeRoot = function () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  };

  /* ---------------------------------------------- EMPFEHLUNGEN ---------------------------------------------- */

  // Antworten bleiben für die Sitzung im Speicher, damit der Drawer beim Neu-Rendern nicht flackert
  var recommendationsCache = new Map();

  class CartDrawerRecommendations extends HTMLElement {
    connectedCallback() {
      var productId = this.getAttribute('product-id');

      if (!productId) {
        this.render(null);
        return;
      }

      var fetchLimit = Math.min(10, this.limit + this.excluded.size);
      var url = routeRoot() + 'recommendations/products?product_id=' + encodeURIComponent(productId) +
        '&limit=' + fetchLimit + '&section_id=cart-drawer-recommendations&intent=' + (this.getAttribute('intent') || 'related');

      if (recommendationsCache.has(url)) {
        this.render(recommendationsCache.get(url));
        return;
      }

      fetch(url)
        .then(function (response) { return response.ok ? response.text() : ''; })
        .catch(function () { return ''; })
        .then(function (html) {
          recommendationsCache.set(url, html);

          if (this.isConnected) {
            this.render(html);
          }
        }.bind(this));
    }

    get limit() {
      return parseInt(this.getAttribute('limit') || '3', 10);
    }

    get excluded() {
      return new Set((this.getAttribute('exclude') || '').split(',').filter(Boolean));
    }

    render(html) {
      var list = this.querySelector('[data-recommendations-list]');

      if (!list) {
        return;
      }

      if (html !== null) {
        var excluded = this.excluded;
        var doc = new DOMParser().parseFromString(html || '', 'text/html');
        var cards = Array.from(doc.querySelectorAll('[data-recommendations-list] > .cdx-card'))
          .filter(function (card) { return !excluded.has(card.getAttribute('data-product-id')); })
          .slice(0, this.limit);

        if (cards.length > 0) {
          list.replaceChildren.apply(list, cards.map(function (card) { return document.importNode(card, true); }));
        } else {
          var fallback = this.querySelector('template[data-fallback]');
          list.replaceChildren(fallback ? document.importNode(fallback.content, true) : '');
        }
      }

      this.hidden = list.querySelector('.cdx-card') === null;
    }
  }

  /* ---------------------------------------------- LIEFERZEIT ---------------------------------------------- */

  var MONTHS_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

  class CartDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      var minDays = parseInt(this.getAttribute('min-days') || '0', 10);
      var maxDays = Math.max(minDays, parseInt(this.getAttribute('max-days') || '0', 10));
      var cutoffHour = parseInt(this.getAttribute('cutoff-hour') || '0', 10);
      // Werktage: "mo-sa" (nur Sonntag frei), "mo-fr" (Samstag + Sonntag frei) oder "all" (jeder Tag zählt)
      var workdays = this.getAttribute('workdays') || (this.hasAttribute('skip-weekends') ? 'mo-fr' : 'all');
      var start = new Date();

      // Nach Bestellschluss beginnt die Zählung erst am nächsten Tag
      if (cutoffHour > 0 && start.getHours() >= cutoffHour) {
        start.setDate(start.getDate() + 1);
      }

      var target = this.querySelector('[data-delivery-range]');

      if (!target) {
        return;
      }

      var from = this.addWorkdays(start, minDays, workdays);
      var to = this.addWorkdays(start, maxDays, workdays);

      target.textContent = minDays === maxDays ? this.format(from) : this.format(from) + ' - ' + this.format(to);
      this.hidden = false;
    }

    isWorkday(date, workdays) {
      var day = date.getDay(); // 0 = Sonntag, 6 = Samstag

      if (workdays === 'all') {
        return true;
      }

      if (workdays === 'mo-sa') {
        return day !== 0;
      }

      return day !== 0 && day !== 6;
    }

    // Zählt ab morgen nur Werktage: heute Do + 2 Werktage (Mo–Sa) = Sa, + 4 Werktage = Di
    addWorkdays(date, days, workdays) {
      var result = new Date(date.getTime());
      var added = 0;

      while (added < days) {
        result.setDate(result.getDate() + 1);

        if (this.isWorkday(result, workdays)) {
          added++;
        }
      }

      // Liegt das Ergebnis (z. B. bei 0 Tagen) auf einem freien Tag, auf den nächsten Werktag schieben
      while (!this.isWorkday(result, workdays)) {
        result.setDate(result.getDate() + 1);
      }

      return result;
    }

    format(date) {
      var locale = this.getAttribute('locale') || document.documentElement.lang || 'de';

      if (locale.toLowerCase().indexOf('de') === 0) {
        return date.getDate() + '. ' + MONTHS_DE[date.getMonth()];
      }

      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date);
    }
  }

  /* ---------------------------------------------- RESERVIERUNG ---------------------------------------------- */

  var RESERVATION_KEY = 'cdx:reservation';

  class CartReservation extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-reservation-time]');
      this.duration = (parseInt(this.getAttribute('minutes') || '10', 10) || 10) * 60000;
      this.cartKey = this.getAttribute('cart-key') || '';
      this.expiredText = this.getAttribute('expired-text') || '';
      this.deadline = this.readDeadline();
      this.tick = this.tick.bind(this);
      this.expired = false;
      this.tick();

      if (!this.expired) {
        this.timer = window.setInterval(this.tick, 1000);
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    /* Startzeit je Sitzung merken, damit der Countdown Seitenwechsel übersteht.
       Ändert sich der Warenkorb, beginnt die Zeit von vorne. */
    readDeadline() {
      var stored = null;

      try {
        stored = JSON.parse(window.sessionStorage.getItem(RESERVATION_KEY) || 'null');
      } catch (error) {
        stored = null;
      }

      if (!stored || stored.key !== this.cartKey || typeof stored.deadline !== 'number') {
        stored = { key: this.cartKey, deadline: Date.now() + this.duration };

        try {
          window.sessionStorage.setItem(RESERVATION_KEY, JSON.stringify(stored));
        } catch (error) {
          /* Kein Sitzungsspeicher (z. B. privater Modus): der Countdown läuft dann nur für diese Ansicht. */
        }
      }

      return stored.deadline;
    }

    tick() {
      var remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        this.expired = true;
        window.clearInterval(this.timer);

        var text = this.querySelector('.cdx-reserve__text');

        if (this.expiredText && text) {
          text.textContent = this.expiredText;
        } else {
          this.hidden = true;
        }

        return;
      }

      if (this.output) {
        var seconds = Math.ceil(remaining / 1000);
        var rest = seconds % 60;

        this.output.textContent = Math.floor(seconds / 60) + ':' + (rest < 10 ? '0' : '') + rest;
      }
    }
  }

  if (!window.customElements.get('cart-drawer-recommendations')) {
    window.customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
  }

  if (!window.customElements.get('cart-delivery-estimate')) {
    window.customElements.define('cart-delivery-estimate', CartDeliveryEstimate);
  }

  if (!window.customElements.get('cart-reservation')) {
    window.customElements.define('cart-reservation', CartReservation);
  }
})();
